import React from "react"
import { InclusionContent } from "@silversea-ssc/ui-components-react"
import { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups"
import * as O from "fp-ts/Option"
import { useECommerceMachine } from "../../../_common/machines/machines-context"
import { FareCard } from "../../_common/read-models/fare-card"
import { CardPromoBadgesContent, FareContent } from "../../../_common/sanity/sanity-types"
import { CruisePaymentTermsResponse } from "../../../_common/dot-com-api-types"
import {
    FarePriceLoaderState,
    useFarePriceLoader,
} from "../../_common/machines/ecommerce-machine/data-loading/fare-price-loader-machine"
import { ExpandableFareCardProvider } from "../../../../components/ExpandableFareCardProvider"
import FarePriceLoaded from "./FarePriceLoaded"
import FarePriceLoading from "./FarePriceLoading"
import { fareInfographicBoxStatus } from "./helpers"

type Props = {
    fareCard: FareCard
    fareContent: FareContent
    paymentTerms: O.Option<CruisePaymentTermsResponse>
    inclusionsContent: InclusionContent[]
    promoBadgesContent: CardPromoBadgesContent[]
    cruiseGroup: CruiseGroupCode
}

const FareCardLoader: React.FC<React.PropsWithChildren<Props>> = ({
    inclusionsContent,
    cruiseGroup,
    paymentTerms,
    fareCard,
    fareContent,
    promoBadgesContent,
}) => {
    const { key: fareKey, index } = fareCard
    const [state] = useECommerceMachine()
    const [statePL] = useFarePriceLoader(state, index)
    const infographicStatus = fareInfographicBoxStatus(fareKey, state)

    if (fareCard && (statePL.matches("loaded") || isRefreshing(statePL))) {
        const price = statePL.context.response
        return (
            <ExpandableFareCardProvider>
                <FarePriceLoaded
                    paymentTerms={paymentTerms}
                    fareCard={fareCard}
                    inclusionsContent={inclusionsContent}
                    status={infographicStatus}
                    cruiseGroup={cruiseGroup}
                    farePrice={price}
                    promoBadgesContent={promoBadgesContent}
                    fareContent={fareContent}
                    isRefreshingFarePrice={isRefreshing(statePL)}
                    faresAreLoading={state.matches("flow.nothingSelected.fareUnselectable")}
                />
            </ExpandableFareCardProvider>
        )
    }

    return (
        <FarePriceLoading
            fareCard={fareCard}
            paymentTerms={paymentTerms}
            status="none"
            promoBadgesContent={promoBadgesContent}
            fareContent={fareContent}
        />
    )
}

// NOTE: this is needed because otherwise you can't `state.matches` another time after the type narrowing
const isRefreshing = (state: FarePriceLoaderState) => state.matches("loading.refresh")

export default FareCardLoader
